const textEncoder = new TextEncoder();

function pemToArrayBuffer(pem) {
  const cleaned = String(pem || '')
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s+/g, '');
  const binary = atob(cleaned);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function toBase64(bytesOrBuffer) {
  const bytes = bytesOrBuffer instanceof Uint8Array
    ? bytesOrBuffer
    : new Uint8Array(bytesOrBuffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function importRsaPublicKey(publicKeyPem) {
  const spki = pemToArrayBuffer(publicKeyPem);
  return crypto.subtle.importKey(
    'spki',
    spki,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  );
}

async function wrapAesKey(aesKey, rsaPublicKey) {
  const rawAesKey = await crypto.subtle.exportKey('raw', aesKey);
  const wrapped = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    rsaPublicKey,
    rawAesKey
  );
  return toBase64(wrapped);
}

async function encryptJson(payload, aesKey, aadText) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const additionalData = textEncoder.encode(aadText);
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      additionalData,
    },
    aesKey,
    textEncoder.encode(JSON.stringify(payload))
  );
  return {
    iv: toBase64(iv),
    ciphertext: toBase64(ciphertext),
  };
}

async function encryptFile(file, aesKey, aadText) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const additionalData = textEncoder.encode(aadText);
  const fileBuffer = await file.arrayBuffer();
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      additionalData,
    },
    aesKey,
    fileBuffer
  );
  return {
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    iv: toBase64(iv),
    ciphertext: toBase64(ciphertext),
  };
}

export async function encryptSubmissionPackage({
  publicKeyPem,
  keyId,
  inviteId,
  payload,
  files,
}) {
  if (!publicKeyPem) {
    throw new Error('Missing onboarding encryption public key.');
  }
  if (!window.crypto?.subtle) {
    throw new Error('Web Crypto API is unavailable in this browser.');
  }

  const rsaPublicKey = await importRsaPublicKey(publicKeyPem);
  const aesKey = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt']
  );

  const aad = `chemcab-onboarding:${inviteId || 'unknown'}`;
  const wrappedKey = await wrapAesKey(aesKey, rsaPublicKey);
  const encryptedPayload = await encryptJson(payload, aesKey, aad);

  const encryptedFiles = [];
  for (let i = 0; i < files.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const encrypted = await encryptFile(files[i], aesKey, aad);
    encryptedFiles.push(encrypted);
  }

  return {
    keyId: keyId || 'default',
    algorithm: 'AES-256-GCM/RSA-OAEP-SHA256',
    aad,
    wrappedKey,
    payload: encryptedPayload,
    files: encryptedFiles,
  };
}
